import React, { ReactElement } from 'react';
import { Box, Divider } from '@mui/material';
import SendOutlined from '@mui/icons-material/SendOutlined';
import {
  MemoStatefulAutocompleteWithText,
  StatefulEmailInput,
} from '../../dataCreationForms/withInputForm/statefulInputsWrapper';
import { ButtonContained, ButtonText } from 'components/general';
import { CircularProgress } from '@mui/material';
import { FormattedMessage } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';

import { useStyles } from './styles';
import { FormFieldProps } from 'components/inspection/Form/types';
import { BaseReportModel } from 'shared/domain/report/types/model/common';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import {
  ReportFieldsContentSelect,
  ReportSelectedFieldsMultiselect,
} from 'components/report/ReportFieldsSelector';
import {
  ReportAdditionalContentEmptyInfo,
  ReportEstimatedSizeBadge,
  ReportFormSectionSubheader,
  REPORT_FORM_FULL_WIDTH_SX,
  REPORT_FORM_GRID_SX,
  REPORT_MODAL_EDGE_BLEED_SX,
  ReportParticipantPersonalDataSection,
} from 'components/dataCreationForms/report/formUi';
import {
  ReportFieldsContent,
  ReportType,
} from 'shared/domain/report/types/model/common';
import {
  isInspectionReportType,
  isIssueReportType,
} from 'shared/domain/report/types/model/reportTypeGuards';

function hasSelectedFieldsMode(values: Record<string, unknown>): boolean {
  const fieldsContent = values.fieldsContent as
    | { _id?: string }
    | undefined;
  return fieldsContent?._id === ReportFieldsContent.selected;
}

interface ReportFormPresentationalProps {
  onCancel: () => void;
  onSubmit: () => void;
  isPosting: boolean;
  recipientsFieldProps: FormFieldProps<BaseReportModel>;
  nameFieldProps: FormFieldProps<BaseReportModel>;
  messageFieldProps: FormFieldProps<BaseReportModel>;
  ContentSelection: () => ReactElement | null;
  messagePresentation: ReactElement;
  reportType: ReportType;
  hasAdditionalContent: boolean;
}

export function ReportFormPresentational(
  props: ReportFormPresentationalProps
): React.ReactElement {
  const {
    onCancel,
    onSubmit,
    isPosting,
    recipientsFieldProps,
    nameFieldProps,
    messageFieldProps,
    ContentSelection,
    messagePresentation,
    hasAdditionalContent,
    reportType,
  } = props;
  const classes = useStyles();
  const { values } = useInputForm();
  const isIssueReport = isIssueReportType(reportType);
  const isInspectionReport = isInspectionReportType(reportType);
  const showSelectedFields =
    isIssueReport && hasSelectedFieldsMode(values as Record<string, unknown>);
  const contentSectionHeaderId = isInspectionReport
    ? 'report_section_report_content'
    : 'report_wizard_step_content';

  return (
    <div className={classes.root} data-qa='report_form'>
      <Box sx={REPORT_FORM_GRID_SX} data-qa='report_form_details'>
        <StatefulEmailInput {...recipientsFieldProps} />
        <ReportParticipantPersonalDataSection />
        {isIssueReport ? (
          <>
            <MemoStatefulAutocompleteWithText {...nameFieldProps} />
            <ReportFieldsContentSelect />
          </>
        ) : (
          <Box sx={REPORT_FORM_FULL_WIDTH_SX}>
            <MemoStatefulAutocompleteWithText {...nameFieldProps} />
          </Box>
        )}
        {showSelectedFields && (
          <Box sx={REPORT_FORM_FULL_WIDTH_SX}>
            <ReportSelectedFieldsMultiselect />
          </Box>
        )}
      </Box>

      <Box data-qa='report_form_message_section'>
        <MemoStatefulAutocompleteWithText {...messageFieldProps} />
        {messagePresentation}
      </Box>

      <Box data-qa='report_form_additional_content' sx={{ mt: 2 }}>
        <Divider sx={REPORT_MODAL_EDGE_BLEED_SX} />
        <ReportFormSectionSubheader messageId={contentSectionHeaderId} />
        {hasAdditionalContent ? (
          <ContentSelection />
        ) : (
          <ReportAdditionalContentEmptyInfo />
        )}
      </Box>

      <Divider sx={{ ...REPORT_MODAL_EDGE_BLEED_SX, mt: 3 }} />
      <div className={classes.submitButtons} data-qa='report_form_actions'>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonText
            onClick={onCancel}
            disabled={isPosting}
            data-qa='report_form_cancel_button'
          >
            <FormattedMessage id={CommonTranslationKeys.cancel} />
          </ButtonText>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReportEstimatedSizeBadge />
          <ButtonContained
            onClick={onSubmit}
            disabled={isPosting}
            data-qa='report_form_send_button'
            startIcon={isPosting ? undefined : <SendOutlined />}
            endIcon={
              isPosting ? (
                <CircularProgress
                  size={13}
                  thickness={4}
                  disableShrink
                  color='secondary'
                />
              ) : undefined
            }
          >
            <FormattedMessage id={CommonTranslationKeys.send} />
          </ButtonContained>
        </Box>
      </div>
    </div>
  );
}
